import { useEffect, useRef, useState } from 'react';
import { Search, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  DRIFT_KINDS,
  DRIFT_SEVERITIES,
  type DriftFilter,
  type DriftKind,
  type DriftSeverity,
} from '@/hooks/useDriftReport';

/**
 * Filter-Toolbar + aktive-Filter-Chips für das Drift-Dashboard (Sprint 8).
 *
 * Reine Präsentations-Komponente: hält keinen Filter-State selbst (der lebt
 * als URL-State in der Route), nur einen Draft für die Freitextsuche, damit
 * beim Tippen nicht jeder Tastendruck sofort navigiert (250ms Debounce).
 */

export const KIND_LABELS: Record<DriftKind, string> = {
  none: 'in sync',
  patch: 'Patch',
  minor: 'Minor',
  major: 'Major',
  unknown: 'unbekannt',
};

export const SEVERITY_LABELS: Record<DriftSeverity, string> = {
  info: 'OK (info)',
  warning: 'Drift (warning)',
  critical: 'Kritisch (critical)',
};

export interface DriftFilterBarProps {
  filter: DriftFilter;
  onChange: (next: DriftFilter) => void;
  customerOptions: { id: string; name: string }[];
  serviceTypeOptions: { slug: string; name: string }[];
  /** Zeilen nach allen Filtern - für die Trefferzahl. */
  resultCount: number;
  /** Zeilen ohne Client-Filter (Backend-Antwort). */
  totalCount: number;
}

const selectClass =
  'rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

export function DriftFilterBar({
  filter,
  onChange,
  customerOptions,
  serviceTypeOptions,
  resultCount,
  totalCount,
}: DriftFilterBarProps): JSX.Element {
  const setKey = <K extends keyof DriftFilter>(
    key: K,
    value: DriftFilter[K] | undefined
  ): void => {
    const next: DriftFilter = { ...filter };
    if (value === undefined || value === '' || value === false) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  // --- Freitext-Draft mit Debounce. lastCommitted unterscheidet eigene
  // Commits von externen Änderungen (Chip entfernt / Reset / Deeplink),
  // damit der Draft beim Tippen nicht vom nachlaufenden URL-State
  // zurückgesetzt wird.
  const [searchDraft, setSearchDraft] = useState(filter.searchText ?? '');
  const lastCommitted = useRef(filter.searchText ?? '');
  useEffect(() => {
    const ext = filter.searchText ?? '';
    if (ext !== lastCommitted.current) {
      lastCommitted.current = ext;
      setSearchDraft(ext);
    }
  }, [filter.searchText]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchDraft === (filter.searchText ?? '')) return;
      lastCommitted.current = searchDraft;
      setKey('searchText', searchDraft || undefined);
    }, 250);
    return () => window.clearTimeout(t);
    // setKey/filter absichtlich nicht in den Deps: der Timeout liest beim
    // Feuern die aktuellen Werte aus dem Closure des letzten Renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Input
            label="Suche"
            placeholder="Hostname, Kunde, Service, Version ..."
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            leftIcon={<Search className="size-4" />}
            data-testid="drift-filter-search"
          />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Kunde</span>
          <select
            value={filter.customerId ?? ''}
            onChange={(e) => setKey('customerId', e.target.value || undefined)}
            className={['min-w-[180px]', selectClass].join(' ')}
            data-testid="drift-filter-customer"
          >
            <option value="">Alle Kunden</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Service-Typ</span>
          <select
            value={filter.serviceType ?? ''}
            onChange={(e) => setKey('serviceType', e.target.value || undefined)}
            className={['min-w-[160px]', selectClass].join(' ')}
            data-testid="drift-filter-service-type"
          >
            <option value="">Alle</option>
            {serviceTypeOptions.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name} ({s.slug})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Severity</span>
          <select
            value={filter.severity ?? ''}
            onChange={(e) =>
              setKey('severity', (e.target.value as DriftSeverity) || undefined)
            }
            className={['min-w-[130px]', selectClass].join(' ')}
            data-testid="drift-filter-severity"
          >
            <option value="">Alle</option>
            {DRIFT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Drift-Typ</span>
          <select
            value={filter.driftKind ?? ''}
            onChange={(e) =>
              setKey('driftKind', (e.target.value as DriftKind) || undefined)
            }
            className={['min-w-[120px]', selectClass].join(' ')}
            data-testid="drift-filter-drift-kind"
          >
            <option value="">Alle</option>
            {DRIFT_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Nicht gesehen seit</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={filter.minAgeDays ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                const num = Number(raw);
                setKey(
                  'minAgeDays',
                  raw === '' || Number.isNaN(num) ? undefined : Math.max(0, Math.floor(num))
                );
              }}
              className={['w-20', selectClass].join(' ')}
              aria-label="Nicht gesehen seit (Tage)"
              data-testid="drift-filter-min-age"
            />
            <span className="text-xs text-muted">Tagen</span>
          </div>
        </label>

        <div className="flex items-center gap-3 self-end pb-1">
          <ToggleCheckbox
            checked={Boolean(filter.driftOnly)}
            onChange={(v) => setKey('driftOnly', v ? true : undefined)}
            label="nur Drift"
            testId="drift-filter-drift-only"
          />
          <ToggleCheckbox
            checked={Boolean(filter.eolOnly)}
            onChange={(v) => setKey('eolOnly', v ? true : undefined)}
            label="nur EOL"
            testId="drift-filter-eol-only"
          />
        </div>
      </div>

      <ActiveFilterChips
        filter={filter}
        onChange={onChange}
        customerOptions={customerOptions}
        serviceTypeOptions={serviceTypeOptions}
        resultCount={resultCount}
        totalCount={totalCount}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aktive-Filter-Chips + Trefferzahl
// ---------------------------------------------------------------------------

interface ChipDef {
  key: keyof DriftFilter;
  label: string;
}

function chipsFor(
  filter: DriftFilter,
  customerOptions: { id: string; name: string }[],
  serviceTypeOptions: { slug: string; name: string }[]
): ChipDef[] {
  const chips: ChipDef[] = [];
  if (filter.searchText?.trim()) {
    chips.push({ key: 'searchText', label: `Suche: "${filter.searchText.trim()}"` });
  }
  if (filter.customerId) {
    const name = customerOptions.find((c) => c.id === filter.customerId)?.name;
    chips.push({ key: 'customerId', label: `Kunde: ${name ?? filter.customerId}` });
  }
  if (filter.serviceType) {
    const name = serviceTypeOptions.find((s) => s.slug === filter.serviceType)?.name;
    chips.push({ key: 'serviceType', label: `Service: ${name ?? filter.serviceType}` });
  }
  if (filter.severity) {
    chips.push({ key: 'severity', label: `Severity: ${SEVERITY_LABELS[filter.severity]}` });
  }
  if (filter.driftKind) {
    chips.push({ key: 'driftKind', label: `Drift-Typ: ${KIND_LABELS[filter.driftKind]}` });
  }
  if (filter.driftOnly) chips.push({ key: 'driftOnly', label: 'nur Drift' });
  if (filter.eolOnly) chips.push({ key: 'eolOnly', label: 'nur EOL' });
  if (filter.minAgeDays !== undefined) {
    chips.push({
      key: 'minAgeDays',
      label: `≥ ${filter.minAgeDays} Tage nicht gesehen`,
    });
  }
  return chips;
}

function ActiveFilterChips({
  filter,
  onChange,
  customerOptions,
  serviceTypeOptions,
  resultCount,
  totalCount,
}: DriftFilterBarProps): JSX.Element {
  const chips = chipsFor(filter, customerOptions, serviceTypeOptions);

  const removeKey = (key: keyof DriftFilter): void => {
    const next: DriftFilter = { ...filter };
    delete next[key];
    onChange(next);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="drift-filter-chips"
    >
      <span className="text-xs text-muted" data-testid="drift-filter-count">
        {chips.length > 0
          ? `${resultCount} von ${totalCount} Zeilen`
          : `${totalCount} Zeilen`}
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary"
          data-testid={`drift-filter-chip-${chip.key}`}
        >
          {chip.label}
          <button
            type="button"
            onClick={() => removeKey(chip.key)}
            className="rounded-full p-0.5 hover:bg-primary/20"
            aria-label={`Filter entfernen: ${chip.label}`}
            data-testid={`drift-filter-chip-remove-${chip.key}`}
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      {chips.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({})}
          leftIcon={<XCircle className="size-4" />}
          data-testid="drift-filter-reset"
        >
          Alle zurücksetzen
        </Button>
      )}
    </div>
  );
}

function ToggleCheckbox({
  checked,
  onChange,
  label,
  testId,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  testId?: string;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-border accent-primary"
        data-testid={testId}
      />
      <span>{label}</span>
    </label>
  );
}
